/**
 * Core
 */
import React from 'react'
import { useState, useEffect, useCallback } from 'react'
import '../assets/scss/pages/_certified-judges.scss'

/**
 * Components
 */
import Footer from '../components/footer'
/**
 * Hooks
 */
import useScrollTrigger from '../hooks/scroll-trigger.hook'


const API = 'https://api.awintern.com/api/v1/judge_certifications/certified_judges';
const IMG_BASE = 'https://awintern.com';

function initials(name) {
    return name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  }

  function JudgeCard({ judge }) {
    const imgSrc = judge.profile_image ? IMG_BASE + judge.profile_image : null;
    const [imgFailed, setImgFailed] = useState(false);
  
    return (
      <article className="cj-card">
        <div className="cj-card__portrait">
          {imgSrc && !imgFailed ? (
            <img
              src={imgSrc}
              alt={judge.name}
              loading="lazy"    
              onError={() => setImgFailed(true)}
            />
          ) : null}
          <div
            className="cj-card__initials"
            style={{ display: imgSrc && !imgFailed ? 'none' : 'flex' }}
          >
            {initials(judge.name)}
          </div>
          <span className="cj-card__verify">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Verified
          </span>
        </div>
        <div className="cj-card__body">
          <h3 className="cj-card__name">{judge.name}</h3>
          <div className="cj-card__org">{judge.organization}</div>
          <p className="cj-card__role">{judge.job_title}</p>
          <div className="cj-card__foot">
            <span className="cj-card__badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="9" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
              Certified Judge
            </span>
          </div>
        </div>
      </article>
    );
  }


  function Hero({ total }) {
    return (
      <div className="cj-hero-band">
        <section className="cj-hero">
          <div>
            <h1>The world's<br /><span className="cj-accent">Certified Judges.</span></h1>
            <p className="cj-hero__lede">
              A living directory of practitioners who have judged and scored well within
              the past 12 months. Standing here is earned, dated and decays without
              participation — every entry below carries a verifiable certification history.
            </p>
          </div>
          <aside className="cj-hero__card">
            <div className="cj-hero__card-label">Currently active</div>
            <div className="cj-hero__card-value">
              {total === null ? <span className="cj-loading">Loading…</span> : total}
            </div>
            <div className="cj-hero__card-sub">
              {total === null ? 'Fetching from registry…' : 'Active certified judges'}
            </div>
          </aside>
        </section>
      </div>
    );
  }


  export default function CertifiedJudges() {
    const [allJudges, setAllJudges] = useState([]);
    const [query, setQuery] = useState('');
    const [sortAsc, setSortAsc] = useState(true);
    const [total, setTotal] = useState(null);
    const [error, setError] = useState(null);
  
    useEffect(() => {
      fetch(API)
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then(data => {
          const certified = data.filter(j => j.certified);
          setAllJudges(certified);
          setTotal(certified.length);
        })
        .catch(err => setError(err.message));
    }, []);
  
    const judges = useCallback(() => {
      const q = query.trim().toLowerCase();
      let list = q
        ? allJudges.filter(j =>
            j.name.toLowerCase().includes(q) ||
            (j.organization || '').toLowerCase().includes(q) ||
            (j.job_title || '').toLowerCase().includes(q)
          )
        : [...allJudges];
      list.sort((a, b) => sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));
      return list;
    }, [allJudges, query, sortAsc]);
  
    const filtered = judges();
    const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  
    return (
      <div className="cj-root">
        <Hero total={total} />
  
        <div className="cj-inner">
  
          {/* Tools */}
          <div className="cj-tools">
            <div className="cj-search">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search name, organisation…"
                autoComplete="off"
              />
            </div>
            <div className="cj-tools__spacer" />
            {allJudges.length > 0 && (
              <span className="cj-results">
                {filtered.length === allJudges.length
                  ? <><b>{allJudges.length}</b> judges</>
                  : <>Showing <b>{filtered.length}</b> of {allJudges.length}</>}
              </span>
            )}
            <button className="cj-sort" onClick={() => setSortAsc(a => !a)}>
              Sort <b>{sortAsc ? 'A → Z' : 'Z → A'}</b>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
  
          {/* Section head */}
          <div className="cj-section-head">
            <h2>Certified Judges</h2>
            <span className="cj-section-head__sub">Active within the last 12 months</span>
            <div className="cj-section-head__spacer" />
            {allJudges.length > 0 && (
              <span className="cj-section-head__count">
                Showing <b>{filtered.length}</b> of {allJudges.length}
              </span>
            )}
          </div>
  
          {/* Grid */}
          <section className="cj-grid">
            {error ? (
              <div className="cj-state cj-state--error">Failed to load judges: {error}</div>
            ) : allJudges.length === 0 ? (
              <div className="cj-state">
                <div className="cj-spinner" />
                <span>Loading certified judges…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="cj-state">No judges match your search.</div>
            ) : (
              filtered.map(j => <JudgeCard key={j.name} judge={j} />)
            )}
          </section>
  
        </div>
  
        {/* Footer */}
            <section className='pb-0 border-t border-t-ai-gray-800'>
                <Footer />
            </section>
      </div>
    );
  }
  
